import { ReactNode, useCallback, useMemo, useState } from 'react';
import { components } from '@api/club/generated';

import { cx } from '@libs/classnames';
import { HorizontalScroll } from '@ui/components/HorizontalScroll';
import { Table, TableCell, TableRow } from '@ui/components/Table';

import { ParticipantStartListRow } from '@features/tournament/ui/components/StartList/ParticipantStartListRow';
import { TeamStartListRow } from '@features/tournament/ui/components/StartList/TeamStartListRow';

import styles from './StartList.module.css';

export type Props = {
  className?: string;
  isManager: boolean;
  isRcf?: boolean;
  highlightedUserId?: string;
  groupTitle?: string;
  renderShieldQuickPeek?: (userId: string) => ReactNode;
  rows?: components['schemas']['PrivateSeedDto'][] | components['schemas']['TeamSeedDto'][];
};

export function StartList({
  className,
  isManager,
  isRcf,
  highlightedUserId,
  groupTitle,
  renderShieldQuickPeek,
  rows,
}: Props) {
  const [visibleTeamIds, setVisibleTeamIds] = useState(() => new Set());
  const handleTeamToggle = useCallback(
    (teamId: string) => {
      const newTeamIds = new Set(visibleTeamIds);
      newTeamIds.has(teamId) ? newTeamIds.delete(teamId) : newTeamIds.add(teamId);
      setVisibleTeamIds(newTeamIds);
    },
    [visibleTeamIds, setVisibleTeamIds],
  );

  const showGroup = !!groupTitle && !isRcf;
  const groupIsRegion = groupTitle === 'Регион';
  const hasTeams = useMemo(
    () => Boolean((rows?.[0] as components['schemas']['TeamSeedDto'] | undefined)?.teamId),
    [rows],
  );

  return (
    <HorizontalScroll>
      <Table className={cx(className, styles.participantsList)}>
        <TableRow header>
          <TableCell className={styles.numColumn} />
          <TableCell className={styles.nameColumn} colSpan={hasTeams ? 2 : 1}>
            Имя
          </TableCell>
          {isManager && <TableCell>Email</TableCell>}
          {showGroup && <TableCell leftGap>{groupTitle}</TableCell>}
          {isRcf && <TableCell leftGap>Регион</TableCell>}
          {isRcf && isManager && (
            <TableCell leftGap rightGap>
              Год рожд.
            </TableCell>
          )}
          <TableCell leftGap>
            {isRcf ? <span className={styles.rcfHeaderText}>Рейтинг ФШР</span> : 'Рейтинг'}
          </TableCell>
        </TableRow>

        {rows?.map((r) => {
          if ('members' in r) {
            const row = r as components['schemas']['TeamSeedDto'];
            return (
              <TeamStartListRow
                key={row.teamId}
                showGroup={showGroup}
                showEmail={isManager}
                isRcf={!!isRcf}
                groupIsRegion={groupIsRegion}
                row={row}
                isToggled={visibleTeamIds.has(row.teamId)}
                onToggle={handleTeamToggle}
                highlightedUserId={highlightedUserId}
                renderShieldQuickPeek={renderShieldQuickPeek}
              />
            );
          } else {
            const row = r as components['schemas']['PrivateSeedDto'];
            return (
              <ParticipantStartListRow
                key={row.userId}
                isInTeam={false}
                showGroup={showGroup}
                showEmail={isManager}
                isRcf={!!isRcf}
                groupIsRegion={groupIsRegion}
                row={row}
                highlightedUserId={highlightedUserId}
                renderShieldQuickPeek={renderShieldQuickPeek}
              />
            );
          }
        })}
      </Table>

      {(!rows || !rows.length) && <div className={styles.placeholder}>Нет участников</div>}
    </HorizontalScroll>
  );
}
